import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SubscriptionGate from '../../components/ui/SubscriptionGate';
import propertyService from '../../services/propertyService';
import projectService from '../../services/projectService';
import ProjectForm from './components/ProjectForm';
import ProjectCard from './components/ProjectCard';
import ProjectTimeline from './components/ProjectTimeline';
import KanbanView from './components/KanbanView';
import CompleteProjectModal from './components/CompleteProjectModal';
import PlanningProjectionChart from './components/PlanningProjectionChart';
import { normalizePlanSlug, PLAN_SLUGS } from '../../config/plans';

const PropertyPlanning = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { formatCurrency } = useCurrency();
  const [currentTier, setCurrentTier] = useState(PLAN_SLUGS.TRIAL);

  // Property selection
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loadingProperties, setLoadingProperties] = useState(true);

  // Projects
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingProject, setCompletingProject] = useState(null);
  const [defaultProjectStatus, setDefaultProjectStatus] = useState('planned');

  // View mode
  const [viewMode, setViewMode] = useState('kanban');

  useEffect(() => {
    if (userProfile) {
      setCurrentTier(normalizePlanSlug(userProfile?.subscription_tier));
    }
  }, [userProfile]);

  // Load only acquired properties and all their projects (PRO only – no data when not PRO)
  useEffect(() => {
    const loadPropertiesAndProjects = async () => {
      const tier = normalizePlanSlug(userProfile?.subscription_tier);
      if (!user?.id || tier !== 'pro') {
        setLoadingProperties(false);
        setLoadingProjects(false);
        return;
      }
      setLoadingProperties(true);
      setLoadingProjects(true);
      try {
        const result = await propertyService.getAll(user.id);
        if (result?.success) {
          const acquiredProperties = (result?.data || []).filter(p => p?.stage === 'acquired');
          setProperties(acquiredProperties);
          if (acquiredProperties.length > 0) {
            const allProjects = [];
            for (const property of acquiredProperties) {
              try {
                const projectsResult = await projectService.getAll(property.id);
                if (projectsResult?.success) {
                  const propertyProjects = (projectsResult?.data || []).map(p => ({
                    ...p,
                    propertyId: property.id,
                    propertyAddress: property.address
                  }));
                  allProjects.push(...propertyProjects);
                }
              } catch (error) {
                console.error(`Error loading projects for property ${property.id}:`, error);
              }
            }
            setProjects(allProjects);
          }
        }
      } catch (error) {
        console.error('Error loading properties:', error);
      } finally {
        setLoadingProperties(false);
        setLoadingProjects(false);
      }
    };

    loadPropertiesAndProjects();
  }, [user?.id, userProfile?.subscription_tier]);

  // Always show all projects (no filter by selected property)
  const displayedProjects = projects;

  const handlePropertyChange = (propertyId) => {
    setSelectedPropertyId(propertyId);
    const property = properties?.find(p => p?.id === propertyId);
    setSelectedProperty(property);
  };

  const handleStageChange = async (newStage) => {
    if (!selectedProperty?.id || !user?.id) return;

    try {
      // Get current property data
      const currentFormData = propertyService.toFormFormat(selectedProperty);

      // Update property stage
      const result = await propertyService.update(selectedProperty.id, {
        ...currentFormData,
        stage: newStage
      }, user.id);

      if (result?.success) {
        // Reload property to get updated data
        const updatedProperty = await propertyService.getById(selectedProperty.id);
        if (updatedProperty?.success) {
          setSelectedProperty(updatedProperty.data);
          // Update in properties list
          setProperties(properties.map(p =>
            p.id === selectedProperty.id ? { ...p, stage: newStage } : p
          ));
        }
      } else {
        console.error('Error updating property stage:', result?.error);
        alert(`Erreur lors de la mise à jour de l'étape: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error updating property stage:', error);
      alert(`Erreur lors de la mise à jour de l'étape: ${error?.message}`);
    }
  };

  const handleCreateProject = (defaultStatus = 'planned') => {
    setEditingProject(null);
    // Set first property as default if none selected
    if (!selectedPropertyId && properties.length > 0) {
      setSelectedPropertyId(properties[0].id);
      setSelectedProperty(properties[0]);
    }
    // Store the default status to set when creating the project
    setDefaultProjectStatus(defaultStatus);
    setShowProjectForm(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const handleSaveProject = async (projectData) => {
    const propertyId = projectData.propertyId || (editingProject ? editingProject.propertyId : selectedPropertyId) || (properties.length > 0 ? properties[0].id : null);
    if (!propertyId || !user?.id) return;

    try {
      let result;
      if (editingProject) {
        result = await projectService.update(editingProject.id, projectData, propertyId, user.id);
      } else {
        result = await projectService.create(projectData, propertyId, user.id);
      }

      if (result?.success) {
        // Reload all projects for all properties
        const allProjects = [];
        for (const property of properties) {
          try {
            const projectsResult = await projectService.getAll(property.id);
            if (projectsResult?.success) {
              const propertyProjects = (projectsResult?.data || []).map(p => ({
                ...p,
                propertyId: property.id,
                propertyAddress: property.address
              }));
              allProjects.push(...propertyProjects);
            }
          } catch (error) {
            console.error(`Error loading projects for property ${property.id}:`, error);
          }
        }
        setProjects(allProjects);

        setShowProjectForm(false);
        setEditingProject(null);
      } else {
        console.error('Error saving project:', result?.error);
        alert(`Erreur lors de la sauvegarde: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error saving project:', error);
      alert(`Erreur lors de la sauvegarde: ${error?.message}`);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    const project = projects.find(p => p.id === projectId);
    if (!project || !user?.id) return;

    // If moving to "completed", open the completion modal
    if (newStatus === 'completed' && project.status !== 'completed') {
      setCompletingProject(project);
      setShowCompleteModal(true);
      // Revert the status change - we'll update it after modal submission
      return;
    }

    const propertyId = project.propertyId || (properties.length > 0 ? properties[0].id : null);
    if (!propertyId) return;

    // Update immediately in the UI
    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === projectId ? { ...p, status: newStatus } : p
      )
    );

    // Save to database without reloading all projects
    try {
      const result = await projectService.update(projectId, {
        ...project,
        status: newStatus
      }, propertyId, user.id);

      if (!result?.success) {
        // Revert the UI change if save failed
        setProjects(prevProjects =>
          prevProjects.map(p =>
            p.id === projectId ? { ...p, status: project.status } : p
          )
        );
        console.error('Error updating project status:', result?.error);
        alert(`Erreur lors de la mise à jour du statut: ${result?.error}`);
      }
    } catch (error) {
      // Revert the UI change if save failed
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === projectId ? { ...p, status: project.status } : p
        )
      );
      console.error('Error updating project status:', error);
      alert(`Erreur lors de la mise à jour du statut: ${error?.message}`);
    }
  };

  const handleCompleteProject = async (projectData) => {
    if (!completingProject || !user?.id) return;

    const propertyId = completingProject.propertyId || (properties.length > 0 ? properties[0].id : null);
    if (!propertyId) return;

    // Update immediately in the UI
    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === completingProject.id ? { ...p, ...projectData } : p
      )
    );

    // Save to database
    try {
      const result = await projectService.update(completingProject.id, projectData, propertyId, user.id);

      if (result?.success) {
        setShowCompleteModal(false);
        setCompletingProject(null);
      } else {
        // Revert the UI change if save failed
        setProjects(prevProjects =>
          prevProjects.map(p =>
            p.id === completingProject.id ? { ...p, status: completingProject.status } : p
          )
        );
        console.error('Error completing project:', result?.error);
        alert(`Erreur lors de la finalisation: ${result?.error}`);
      }
    } catch (error) {
      // Revert the UI change if save failed
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === completingProject.id ? { ...p, status: completingProject.status } : p
        )
      );
      console.error('Error completing project:', error);
      alert(`Erreur lors de la finalisation: ${error?.message}`);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet?')) return;

    try {
      const result = await projectService.delete(projectId, user?.id);
      if (result?.success) {
        // Update immediately in the UI
        setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      } else {
        console.error('Error deleting project:', result?.error);
        alert(`Erreur lors de la suppression: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert(`Erreur lors de la suppression: ${error?.message}`);
    }
  };

  const isPro = currentTier === 'pro';
  const portfolioValue = properties?.reduce((sum, p) => sum + (parseFloat(p?.purchase_price) || 0), 0) || 0;
  const portfolioEquity = properties?.reduce((sum, p) => sum + (parseFloat(p?.purchase_price) || 0) - (parseFloat(p?.mortgage_amount) || 0), 0) || 0;
  const portfolioCashflow = properties?.reduce((sum, p) => sum + (parseFloat(p?.monthly_cashflow) || 0), 0) || 0;
  const portfolioRevenue = properties?.reduce((sum, p) => sum + (parseFloat(p?.total_monthly_revenue) || 0), 0) || 0;

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      <NavigationHeader subscriptionTier={currentTier} />

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10" style={{ paddingTop: 'calc(64px + 2rem)' }}>
        {/* PRO gate: show overlay for non-PRO; content behind remains visible but data not loaded */}
        {!isPro && (
          <SubscriptionGate
            requiredTier="pro"
            featureName="le module Planification des projets"
            onUpgrade={() => navigate('/subscription-pricing')}
            onClose={() => navigate('/main-dashboard')}
          />
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="ghost"
              size="icon"
              iconName="ArrowLeft"
              onClick={() => navigate('/main-dashboard')}
            />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Planification des projets
              </h1>
              <p className="text-muted-foreground">
                Planifiez vos projets de rénovation sur vos propriétés acquises : budget, gain en valeur, augmentation des revenus et échéancier
              </p>
            </div>
          </div>
        </div>

        {/* Portefeuille actuel KPIs (PRO only, when properties loaded) */}
        {isPro && properties?.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-5 md:p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
              <Icon name="PieChart" size={20} className="text-primary" />
              Portefeuille actuel
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Valeur du portefeuille</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(portfolioValue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Équité</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(portfolioEquity)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Cashflow mensuel</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(portfolioCashflow)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Revenus mensuels</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(portfolioRevenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Nombre d&apos;immeubles</p>
                <p className="text-lg font-semibold text-foreground">{properties.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {loadingProperties ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        ) : properties?.length === 0 ? (
          <div className="bg-card border-2 border-dashed border-border rounded-xl p-12 text-center mb-8">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Home" size={40} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">
                Aucune propriété acquise
              </h3>
              <p className="text-muted-foreground mb-2 text-base">
                La planification est disponible uniquement pour les propriétés acquises
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Convertissez une propriété analysée en propriété acquise pour commencer la planification
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="default"
                  iconName="BarChart3"
                  iconPosition="left"
                  size="lg"
                  onClick={() => navigate('/main-dashboard')}
                  className="text-base px-8 py-3"
                >
                  Voir mes analyses
                </Button>
                <Button
                  variant="outline"
                  iconName="Plus"
                  iconPosition="left"
                  size="lg"
                  onClick={() => navigate('/property-analysis-wizard')}
                  className="text-base px-8 py-3"
                >
                  Nouvelle analyse
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* 30-year projection chart (PRO only) - Moved here to appear after KPIs */}
        {isPro && properties?.length > 0 && (
          <div className="mb-8">
            <PlanningProjectionChart
              properties={properties}
              projects={displayedProjects}
              formatCurrency={formatCurrency}
            />
          </div>
        )}

        {/* Projects Section */}
        {properties?.length > 0 && (
          <div className="space-y-6">
            {/* View Mode Toggle */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Projets de rénovation
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Suivre vos projets d'amélioration pour toutes vos propriétés acquises
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Calendar"
                  onClick={() => setViewMode('kanban')}
                >
                  Kanban
                </Button>
                <Button
                  variant={viewMode === 'projects' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Grid"
                  onClick={() => setViewMode('projects')}
                >
                  Liste
                </Button>
                <Button
                  variant={viewMode === 'timeline' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Calendar"
                  onClick={() => setViewMode('timeline')}
                >
                  Échéancier
                </Button>
              </div>
            </div>

            {/* Status Summary Cards - Only show for Kanban view */}
            {viewMode === 'kanban' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { id: 'planned', label: 'Planifié', icon: 'Calendar', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' },
                  { id: 'in_progress', label: 'En cours', icon: 'Wrench', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' },
                  { id: 'on_hold', label: 'En attente', icon: 'Clock', color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.1)' },
                  { id: 'completed', label: 'Terminé', icon: 'CheckCircle', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)' }
                ].map(status => {
                  const count = displayedProjects.filter(p => (p?.status || 'planned') === status.id).length;
                  return (
                    <div
                      key={status.id}
                      style={{
                        background: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-md)'
                      }}
                    >
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: 'var(--radius-md)',
                          background: status.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Icon name={status.icon} size={24} style={{ color: status.color }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '0.75rem',
                          color: 'var(--color-muted-foreground)',
                          margin: 0,
                          marginBottom: '4px'
                        }}>
                          {status.label}
                        </p>
                        <p style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: 'var(--color-foreground)',
                          margin: 0
                        }}>
                          {count}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Projects View */}
            {loadingProjects ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : displayedProjects?.length === 0 ? (
              <div className="bg-card border-2 border-dashed border-border rounded-xl p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="FolderPlus" size={40} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">
                    Aucun projet d'amélioration
                  </h3>
                  <p className="text-muted-foreground mb-2 text-base">
                    Planifiez vos projets de rénovation et d'amélioration
                  </p>
                  <p className="text-sm text-muted-foreground mb-8">
                    Suivez les coûts, les délais et calculez le ROI de vos projets
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      size="lg"
                      onClick={handleCreateProject}
                      className="text-base px-8 py-3"
                    >
                      Créer mon premier projet
                    </Button>
                  </div>
                  <div className="mt-8 pt-8 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-3">💡 Types de projets que vous pouvez créer :</p>
                    <ul className="text-xs text-muted-foreground text-left max-w-sm mx-auto space-y-1">
                      <li>• Rénovations (cuisine, salle de bain, etc.)</li>
                      <li>• Améliorations énergétiques</li>
                      <li>• Agrandissements</li>
                      <li>• Améliorations locatives</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : viewMode === 'kanban' ? (
              <KanbanView
                projects={displayedProjects}
                property={selectedProperty}
                properties={properties}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onStatusChange={handleStatusChange}
                onCreateProject={handleCreateProject}
              />
            ) : viewMode === 'timeline' ? (
              <ProjectTimeline
                projects={displayedProjects}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayedProjects?.map(project => {
                  const projectProperty = properties.find(p => p.id === project.propertyId);
                  return (
                    <ProjectCard
                      key={project?.id}
                      project={project}
                      property={projectProperty}
                      onEdit={() => handleEditProject(project)}
                      onDelete={() => handleDeleteProject(project?.id)}
                    />
                  );
                })}
              </div>
            )}

          </div>
        )}

        {/* Project Form Modal */}
        {showProjectForm && properties?.length > 0 && (
          <ProjectForm
            project={editingProject ? editingProject : { status: defaultProjectStatus }}
            property={editingProject ? properties.find(p => p.id === editingProject.propertyId) : (selectedProperty || properties[0])}
            properties={properties}
            onSave={handleSaveProject}
            onCancel={() => {
              setShowProjectForm(false);
              setEditingProject(null);
              setDefaultProjectStatus('planned');
            }}
          />
        )}

        {/* Complete Project Modal */}
        {showCompleteModal && completingProject && (
          <CompleteProjectModal
            project={completingProject}
            property={properties.find(p => p.id === completingProject.propertyId) || properties[0]}
            onSave={handleCompleteProject}
            onCancel={() => {
              setShowCompleteModal(false);
              setCompletingProject(null);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default PropertyPlanning;
